import React, { CSSProperties } from "react";
import { AppShell, Burger, rem, ScrollArea } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { Header } from "./Header";
import { useMediaQuery } from "@mantine/hooks";
import { ZIndex } from "../../utils/zIndex";
import { closeDrawer, toggleDrawer } from "../../store/slices/drawerSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";

interface MyComponentStyles extends CSSProperties {
  fullScreen: boolean;
  fixedPadding?: string;
}

const useStyles = createStyles(
  (theme, { fullScreen, fixedPadding }: MyComponentStyles) => ({
    root: {
      padding: fixedPadding ?? "0px 20px",
      maxWidth: fullScreen ? "2500px" : "1600px",
      margin: "0 auto",

      "@media (max-width: 1024px)": {
        padding: "10px 10px",
      },
    },
    mobileNavbar: {
      position: "fixed",
      top: 0,
      left: 0,
      height: "100vh",
      width: "60vw",
      backgroundColor: theme.white,
      boxShadow: theme.shadows.md,
      transition: "transform 0.3s ease-in-out",
      transform: "translateX(-100%)",
      zIndex: ZIndex.modal,
    },
    mobileNavbarOpen: {
      transform: "translateX(0%)",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      zIndex: ZIndex.base,
      transition: "opacity 0.3s ease-in-out",
    },
  })
);

type Props = {
  children: React.ReactNode;
  fullScreen?: boolean;
  fixedPadding?: string;
  navbar?: React.ReactNode;
};

export const GenderLabAppShell: React.FC<Props> = ({
  children,
  fullScreen = true,
  fixedPadding,
  navbar,
}) => {
  const isMobile = useMediaQuery("(max-width: 992px)");
  const { classes, cx } = useStyles({ fullScreen, fixedPadding });
  const dispatch = useDispatch();
  const drawerOpened = useSelector((state: RootState) => state.drawer.isOpen);
  const headerHeight = 80;

  return (
    <AppShell
      p="md"
      header={{ height: headerHeight }}
      navbar={{
        width: navbar ? 300 : 0,
        breakpoint: "sm",
        collapsed: { mobile: true, desktop: false },
      }}
    >
      <Header />

      {isMobile && navbar && (
        <>
          <Burger
            opened={drawerOpened}
            onClick={() => dispatch(toggleDrawer())}
            size="sm"
            style={{
              position: "absolute",
              top: `calc(${rem(headerHeight)} + var(--mantine-spacing-md))`,
              left: rem(15),
            }}
          />
          {drawerOpened && (
            <button
              className={classes.overlay}
              onClick={() => dispatch(closeDrawer())}
            />
          )}
          <div className={cx(classes.mobileNavbar, { [classes.mobileNavbarOpen]: drawerOpened })}>
            <ScrollArea h="100vh">{navbar}</ScrollArea>
          </div>
        </>
      )}

      {!isMobile && navbar && (
        <AppShell.Navbar>
          <ScrollArea h="100vh">{navbar}</ScrollArea>
        </AppShell.Navbar>
      )}

      <AppShell.Main>
        <div className={classes.root}>
          {children}
        </div>
      </AppShell.Main>
    </AppShell>
  );
};
